import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EdiDespatchAdvice, EdiOrderMessageRelations } from '../../../models/edi';
import { EdiDispatchAdvicesActionConfigService } from '../../../services/edi-dispatch-advices-action-config/edi-dispatch-advices-action-config.service';
import { EdiDispatchAdviceDetailConfigService } from '../../../services/edi-dispatch-detail-config/edi-dispatch-detail-config.service';
import { EdiService } from '../../../services/edi/edi.service';
import { EdiNavigationService } from '../../../services/edi/edi-navigation.service';
import { EdiRowTable, EdiRowTableType } from '../edi-row-table/edi-row-table.component';
import { DateUtilService } from 'gung-common';
import { first } from 'rxjs';
import { BackendFeatureService } from '../../../services/backend-feature.service';
import { EDI_VIEWER_FEATURE_ID } from '../../../services/edi/edi-viewer-compatibility.service';

interface DispatchQuantitySummary {
  unit: string;
  value: number;
}

interface DispatchOrderSummary {
  id: string;
  lineCount: number;
  productCount: number;
  packageCount: number;
  quantities: DispatchQuantitySummary[];
}

interface DispatchSummary {
  orderCount: number;
  uniqueProductCount: number;
  packageCount: number;
  topLevelPackageCount: number;
  nestedPackageCount: number;
  quantities: DispatchQuantitySummary[];
  orderSummaries: DispatchOrderSummary[];
}

@Component({
  selector: 'lib-edi-dispatch-advice-detail',
  templateUrl: './edi-dispatch-advice-detail.component.html',
  styleUrls: ['./edi-dispatch-advice-detail.component.css'],
  standalone: false
})
export class EdiDispatchAdviceDetailComponent implements OnInit {
  // Outgoing messages name their ERP order as the supplier order when erpIds is not filled in
  get erpOrderIds(): string[] {
    if (this.dispatchAdviceData?.erpIds?.length) {
      return this.dispatchAdviceData.erpIds;
    }
    const supplierOrderId = this.dispatchAdviceData?.documentReferences?.supplierOrder?.id;
    return supplierOrderId ? [supplierOrderId] : [];
  }

  relations: EdiOrderMessageRelations | null = null;

  protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  protected ediService = inject(EdiService);
  public ediNavigationService = inject(EdiNavigationService);
  public configService = inject(EdiDispatchAdviceDetailConfigService);
  public actionConfig = inject(EdiDispatchAdvicesActionConfigService);
  protected translateService = inject(TranslateService);
  public dateUtilService = inject(DateUtilService);
  protected backendFeatureService = inject(BackendFeatureService);
  documentViewerEnabled$ = this.backendFeatureService.isActivated(EDI_VIEWER_FEATURE_ID);
  
  dispatchAdviceId: string;
  dispatchAdviceData: any;
  orderId: string;
  dispatchSummary: DispatchSummary = this.emptyDispatchSummary();
  ediNavigationBaseRoute: string;

  showDispatchParcelManagement: boolean = false;

  get itemCount(): number {
    return this.dispatchAdviceData?.items?.length || 0;
  }

  get parcelCount(): number {
    const parcelIds = new Set<string>();
    let parcelsWithoutId = 0;
    for (const item of this.dispatchAdviceData?.items || []) {
      for (const parcel of item?.parcels || []) {
        const parcelId = parcel?.ginIdentifier || parcel?.id;
        if (parcelId) parcelIds.add(parcelId);
        else parcelsWithoutId++;
      }
    }
    return parcelIds.size + parcelsWithoutId;
  }

  get hasMultipleOrders(): boolean {
    return this.dispatchSummary.orderCount > 1;
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 }).format(value);
  }

  formatItemIds(item: EdiDespatchAdvice): string {
    const ids = item?.ids;
    return [
      ids?.gungId ? `${this.translateService.instant('ID')}: ${ids.gungId}` : '',
      ids?.ean13 ? `${this.translateService.instant('EAN')}: ${ids.ean13}` : '',
      ids?.upca ? `${this.translateService.instant('UPCA')}: ${ids.upca}` : '',
      ids?.buyerId ? `${this.translateService.instant('BUYER_ID')}: ${ids.buyerId}` : ''
    ].filter(Boolean).join('\n');
  }

  itemsColumns: EdiRowTable[] = [
    {
      header: 'IMAGE',
      type: EdiRowTableType.IMAGE,
      getValue: (item: EdiDespatchAdvice) => item?.extra?.images
    },
    {
      header: 'ITEM_IDS',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiDespatchAdvice) => this.formatItemIds(item),
      customClass: 'formatted'
    },
    {
      header: 'NAME',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiDespatchAdvice) => item.name
    },
    {
      header: 'PACKAGE_SSCC',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiDespatchAdvice) => {
        let str = '';
        for (const parcel of item.parcels || []) {
          if (!!str) {
            str += '\n';
          }
          str += parcel.ginIdentifier + ' : ' + this.translateService.instant('QTY', {}) + ' ' + parcel.quantity;
        }
        return str;
      },
      customClass: 'formatted'
    },
    {
      header: 'ORDER_NO',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiDespatchAdvice) => item.documentReferences?.buyerOrder?.id
    },
    {
      header: 'QUANTITY',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiDespatchAdvice) => item.quantities?.actualQuantity
    }
  ];


  ngOnInit(): void {
    this.route.params.subscribe(data => {
      this.dispatchAdviceId = data.id;

      const urlSegments = this.router.url.split('?')[0].split('/');
      this.ediNavigationBaseRoute = urlSegments.slice(0, -1).join('/');
      this.ediNavigationService.setNavigationCurrentId(this.ediNavigationBaseRoute, this.dispatchAdviceId);

      this.ediService.getDispatchAdvice(this.dispatchAdviceId).subscribe(dispatchAdviceData => {
        this.dispatchAdviceData = dispatchAdviceData.data;
        this.ediService
          .getDocumentRelations(this.dispatchAdviceData.orderMessageId)
          .pipe(first())
          .subscribe(relations => (this.relations = relations));
        this.dispatchSummary = this.buildDispatchSummary(this.dispatchAdviceData);

        this.showDispatchParcelManagement = false;
        for (const error of this.dispatchAdviceData.errors || []) {
          if (error?.error?.includes('Despatch advice requires SSCC')) {
            this.showDispatchParcelManagement = true;
          }
        }
      });
    });
  }

  saveData(data: EdiDespatchAdvice): void {
    this.ediService.putDispatchAdvice(data.id, data).pipe(first()).subscribe(res => {
      this.dispatchAdviceData = res.data;
      this.dispatchSummary = this.buildDispatchSummary(this.dispatchAdviceData);
    });
  }

  private emptyDispatchSummary(): DispatchSummary {
    return {
      orderCount: 0,
      uniqueProductCount: 0,
      packageCount: 0,
      topLevelPackageCount: 0,
      nestedPackageCount: 0,
      quantities: [],
      orderSummaries: []
    };
  }

  private buildDispatchSummary(message: any): DispatchSummary {
    const items = message?.items || [];
    const products = new Set<string>();
    const parcels = this.collectParcels(items);
    const quantities = new Map<string, number>();
    const orderItems = new Map<string, any[]>();
    const identifiedOrderIds = new Set<string>();

    items.forEach((item, index) => {
      products.add(this.productKey(item, index));
      const quantity = this.toNumber(item?.quantities?.actualQuantityDelivered) ?? this.toNumber(item?.quantities?.actualQuantity);
      this.addToTotal(quantities, String(item?.unitOfMeasure || '').trim(), quantity);

      const orderId = this.orderIdFor(item, message);
      if (orderId !== 'Unassigned') identifiedOrderIds.add(orderId);
      const groupedItems = orderItems.get(orderId) || [];
      groupedItems.push(item);
      orderItems.set(orderId, groupedItems);
    });

    if (!items.length) {
      const headerOrderId = this.orderIdFor(null, message);
      if (headerOrderId !== 'Unassigned') identifiedOrderIds.add(headerOrderId);
    }

    const parcelIdentifiers = new Set(
      Array.from(parcels.values())
        .map(parcel => this.parcelIdentifier(parcel))
        .filter((identifier): identifier is string => !!identifier)
    );
    let topLevelPackageCount = 0;
    for (const parcel of parcels.values()) {
      const parentId = String(parcel?.parentGinIdentifier || '').trim();
      if (!parentId || !parcelIdentifiers.has(parentId)) topLevelPackageCount++;
    }

    const orderSummaries = Array.from(orderItems, ([id, groupedItems]) => ({
      id,
      lineCount: groupedItems.length,
      productCount: new Set(groupedItems.map((item, index) => this.productKey(item, index))).size,
      packageCount: this.collectParcels(groupedItems).size,
      quantities: this.quantityTotals(groupedItems)
    }));

    return {
      orderCount: identifiedOrderIds.size,
      uniqueProductCount: products.size,
      packageCount: parcels.size,
      topLevelPackageCount,
      nestedPackageCount: Math.max(0, parcels.size - topLevelPackageCount),
      quantities: this.mapTotals(quantities),
      orderSummaries
    };
  }

  private quantityTotals(items: any[]): DispatchQuantitySummary[] {
    const totals = new Map<string, number>();
    for (const item of items) {
      const quantity = this.toNumber(item?.quantities?.actualQuantityDelivered) ?? this.toNumber(item?.quantities?.actualQuantity);
      this.addToTotal(totals, String(item?.unitOfMeasure || '').trim(), quantity);
    }
    return this.mapTotals(totals);
  }

  private collectParcels(items: any[]): Map<string, any> {
    const parcels = new Map<string, any>();
    items.forEach((item, itemIndex) => {
      (item?.parcels || []).forEach((parcel, parcelIndex) => {
        const identifier = this.parcelIdentifier(parcel);
        const key = identifier ? `identified:${identifier}` : `anonymous:${itemIndex}:${parcelIndex}`;
        if (!parcels.has(key)) parcels.set(key, parcel);
      });
    });
    return parcels;
  }

  private parcelIdentifier(parcel: any): string {
    return String(parcel?.ginIdentifier || parcel?.id || '').trim();
  }

  private productKey(item: any, fallbackIndex: number): string {
    return String(item?.ids?.gungId || item?.ids?.ean13 || item?.ids?.upca || item?.ids?.buyerId || `line:${fallbackIndex}`);
  }

  private orderIdFor(item: any, message: any): string {
    const references = item?.documentReferences;
    const headerReferences = message?.documentReferences;
    return String(
      references?.buyerOrder?.id ||
      references?.buyerOrder?.externalId ||
      references?.supplierOrder?.id ||
      references?.supplierOrder?.externalId ||
      headerReferences?.buyerOrder?.id ||
      headerReferences?.buyerOrder?.externalId ||
      headerReferences?.supplierOrder?.id ||
      headerReferences?.supplierOrder?.externalId ||
      ''
    ).trim() || 'Unassigned';
  }

  private addToTotal(totals: Map<string, number>, key: string, value: number | null): void {
    if (value === null) return;
    totals.set(key, (totals.get(key) || 0) + value);
  }

  private mapTotals(totals: Map<string, number>): DispatchQuantitySummary[] {
    return Array.from(totals, ([unit, value]) => ({ unit, value }));
  }

  private toNumber(value: unknown): number | null {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }
}
